/**
 * Utility functions for handling iframe resizing
 */

import type { ResizeMessage } from './iframeContentResize';

export { setupIframeContentResize } from './iframeContentResize';

/**
 * Set up iframe resize handling
 * @param iframe - The iframe element to handle resizing for
 * @param onResize - Callback when the iframe requests a resize
 * @param elementId - Optional element ID for targeted messages
 * @returns Cleanup function to remove event listeners
 */
export function setupIframeResize(
  iframe: HTMLIFrameElement,
  onResize: (height: number) => void,
  elementId?: string
): () => void {
  if (!iframe?.contentWindow) {
    console.warn('Iframe or its contentWindow is not available');
    return () => {};
  }

  console.log('Setting up iframe resize handler for element:', elementId);

  // Set up message handler for resize events
  const messageHandler = (event: MessageEvent<ResizeMessage>) => {
    try {
      // Only process messages from our iframe
      if (event.source !== iframe.contentWindow) {
        return;
      }

      // Only process resize messages with valid height
      if (event.data?.type !== 'resize' || typeof event.data?.height !== 'number') {
        return;
      }

      // If elementId is provided, only process messages for this element
      if (elementId && event.data.elementId !== elementId) {
        return;
      }

      console.log('Received resize message:', event.data);
      onResize(event.data.height);
    } catch (error) {
      console.error('Error handling resize message:', error);
    }
  };

  // Function to request height update
  const requestHeightUpdate = () => {
    try {
      if (iframe?.contentWindow) {
        const message: ResizeMessage = { type: 'getHeight' };
        if (elementId) {
          message.elementId = elementId;
        }
        console.log('Requesting height update for element:', elementId);
        iframe.contentWindow.postMessage(message, '*');
      }
    } catch (error) {
      console.error('Error requesting height update:', error);
    }
  };

  // Set up interval to request height updates
  const intervalId = setInterval(requestHeightUpdate, 100);
  
  // Initial request
  requestHeightUpdate();

  // Add event listener
  window.addEventListener('message', messageHandler);

  // Return cleanup function
  return () => {
    console.log('Cleaning up iframe resize handler for element:', elementId);
    window.removeEventListener('message', messageHandler);
    clearInterval(intervalId);
  };
}
